import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThemeEngine } from './ThemeEngineContext';
import rampDefaults from './rampDefaults.json';

/**
 * Resolved token values for code that CSS cannot reach - recharts props, Google
 * Maps marker HTML, canvas drawing, exported documents.
 *
 * This replaces the `document.documentElement.classList.contains('dark')` +
 * MutationObserver block that is currently copy-pasted into 27 files. Those all
 * ask a yes/no question ("is it dark?"), which stops being enough the moment a
 * theme can re-tint a palette: two users in dark mode no longer share a color.
 * Ask for the value instead.
 *
 *   const { color, isDark } = useThemeTokens();
 *   <Bar fill={color('sem-accent')} />
 */

let sharedIsDark: boolean | null = null;
const listeners = new Set<(isDark: boolean) => void>();
let observer: MutationObserver | null = null;

function readIsDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/**
 * One observer for the whole app, not one per component. Starts on the first
 * subscriber and stops when the last one leaves.
 */
function subscribe(listener: (isDark: boolean) => void): () => void {
  listeners.add(listener);

  if (!observer && typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
    sharedIsDark = readIsDark();
    observer = new MutationObserver(() => {
      const next = readIsDark();
      if (next === sharedIsDark) return;      // attribute churn that isn't ours
      sharedIsDark = next;
      listeners.forEach((l) => l(next));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && observer) {
      observer.disconnect();
      observer = null;
      sharedIsDark = null;
    }
  };
}

/** Exposed for tests, which need the module-level observer torn down. */
export function __resetThemeTokenObserver(): void {
  observer?.disconnect();
  observer = null;
  sharedIsDark = null;
  listeners.clear();
}

const RAMP = rampDefaults as Record<string, string>;

export interface ThemeTokens {
  isDark: boolean;
  /** Resolved `R G B` triplet for a token, or '' when it is unknown. */
  triplet: (token: string) => string;
  /** Same value as a CSS color, ready for a chart prop or an SVG attribute. */
  color: (token: string, alpha?: number) => string;
}

export function useThemeTokens(): ThemeTokens {
  const { resolved } = useThemeEngine();
  const [isDark, setIsDark] = useState<boolean>(() => sharedIsDark ?? readIsDark());

  useEffect(() => {
    setIsDark(readIsDark());
    return subscribe(setIsDark);
  }, []);

  const overrides = useMemo(() => {
    const mode = isDark ? 'dark' : 'light';
    return { ...resolved.tokens[mode], ...resolved.components[mode] };
  }, [resolved, isDark]);

  const triplet = useCallback((token: string): string => {
    const seen = new Set<string>();
    let name = token;

    // Tokens alias each other (cmp -> sem -> ramp), so follow the chain. The
    // theme's own overrides win at every hop.
    for (let i = 0; i < 12; i += 1) {
      if (seen.has(name)) return '';         // cycle - give up rather than hang
      seen.add(name);

      const value = overrides[name] ?? RAMP[stripRampPrefix(name)] ?? readCssVar(name);
      if (!value) return '';

      const alias = /^var\(\s*--([a-z0-9-]+)\s*\)$/i.exec(value.trim());
      if (!alias) return value.trim();
      name = alias[1];
    }
    return '';
  }, [overrides]);

  const color = useCallback((token: string, alpha = 1): string => {
    const value = triplet(token);
    if (!value) return 'transparent';
    if (!/^\d{1,3} \d{1,3} \d{1,3}$/.test(value)) return value;   // already a color
    return alpha === 1 ? `rgb(${value})` : `rgb(${value} / ${alpha})`;
  }, [triplet]);

  return { isDark, triplet, color };
}

/** `c-gray-700` -> `gray-700`, so ramp defaults can be looked up directly. */
function stripRampPrefix(name: string): string {
  return name.startsWith('c-') ? name.slice(2) : name;
}

/**
 * Fall back to what the browser actually computed. This is what picks up the
 * semantic and component defaults, which live in CSS rather than in the theme
 * document, plus anything ThemeContext writes inline.
 */
function readCssVar(name: string): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return '';
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  } catch {
    return '';
  }
}
